import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  analyzeReferrer,
  extractUTMParams,
  analyzeUserAgent,
  getOrCreateVisitorId,
  isNewSession,
} from '../utils/referrerAnalyzer';

interface PageVisitData {
  visitor_id: string;
  page: string;
  referrer_url: string | null;
  referrer_source: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  page_path: string;
  user_agent: string;
  device_type: string;
  browser: string;
  os: string;
}

/**
 * 페이지 방문 추적 훅
 * 모든 페이지 전환 시 자동으로 방문 기록을 Supabase에 저장
 */
export function usePageTracking() {
  const location = useLocation();
  const hasTrackedInitialVisit = useRef(false);

  useEffect(() => {
    const trackPageVisit = async () => {
      try {
        // 첫 방문이거나 새로운 세션인 경우에만 추적
        if (!hasTrackedInitialVisit.current || isNewSession()) {
          hasTrackedInitialVisit.current = true;

          const visitorId = getOrCreateVisitorId();
          const referrerUrl = document.referrer;
          const currentUrl = window.location.href;
          const userAgent = navigator.userAgent;

          // Referrer 분석
          const { source: referrerSource } = analyzeReferrer(referrerUrl);

          // UTM 파라미터 추출
          const utmParams = extractUTMParams(currentUrl);

          // User Agent 분석
          const { deviceType, browser, os } = analyzeUserAgent(userAgent);

          // 방문 데이터 구성
          const visitData: PageVisitData = {
            visitor_id: visitorId,
            page: location.pathname + location.search,
            referrer_url: referrerUrl || null,
            referrer_source: referrerSource,
            utm_source: utmParams.utm_source || utmParams.ref,
            utm_medium: utmParams.utm_medium,
            utm_campaign: utmParams.utm_campaign,
            utm_content: utmParams.utm_content,
            utm_term: utmParams.utm_term,
            page_path: location.pathname + location.search,
            user_agent: userAgent,
            device_type: deviceType,
            browser,
            os,
          };

          // Supabase에 저장
          const { error } = await supabase
            .from('page_visits')
            .insert(visitData);

          if (error) {
            console.error('페이지 방문 추적 오류:', error);
          }
        }
      } catch (error) {
        console.error('페이지 방문 추적 중 오류 발생:', error);
      }
    };

    trackPageVisit();
  }, [location.pathname, location.search]);
}
